import React, { useState } from 'react';
import { Plus, Trash2, ArrowUp, ArrowDown, Save, Eye } from 'lucide-react';
import { contracts, contractCategories } from '../../data/contracts';
import { toast } from 'react-hot-toast';

type QuestionType = 'text' | 'textarea' | 'select' | 'radio' | 'checkbox' | 'date' | 'file';

interface QuestionOption {
  value: string;
  label: string;
}

interface Question {
  id: string;
  type: QuestionType;
  label: string;
  required: boolean;
  placeholder?: string;
  helpText?: string;
  options?: QuestionOption[];
}

interface Questionnaire {
  id: string;
  contractId: string;
  title: string;
  description: string;
  questions: Question[];
}

// Mock questionnaires data
const mockQuestionnaires: Questionnaire[] = [
  {
    id: '1',
    contractId: '1',
    title: 'Questionnaire Freelance Overeenkomst',
    description: 'Ce questionnaire nous permettra de personnaliser votre contrat de freelance selon vos besoins spécifiques.',
    questions: [
      {
        id: '1-1',
        type: 'text',
        label: 'Nom complet du freelance',
        required: true,
        placeholder: 'Ex: Jean Dupont',
      },
      {
        id: '1-2',
        type: 'text',
        label: 'Adresse du freelance',
        required: true,
        placeholder: 'Adresse complète',
      },
      {
        id: '1-3',
        type: 'textarea',
        label: 'Description détaillée des services à fournir',
        required: true,
        placeholder: 'Décrivez précisément les services que le freelance doit fournir',
        helpText: 'Soyez aussi précis que possible pour éviter tout malentendu ultérieur',
      },
      {
        id: '1-4',
        type: 'date',
        label: 'Date de début de la mission',
        required: true,
      },
      {
        id: '1-5',
        type: 'select',
        label: 'Durée prévue de la mission',
        required: true,
        options: [
          { value: 'less-than-1-month', label: 'Moins d\'un mois' },
          { value: '1-3-months', label: '1 à 3 mois' },
          { value: '3-6-months', label: '3 à 6 mois' },
          { value: '6-12-months', label: '6 à 12 mois' },
          { value: 'more-than-12-months', label: 'Plus de 12 mois' },
        ],
      },
      {
        id: '1-6',
        type: 'radio',
        label: 'Mode de rémunération',
        required: true,
        options: [
          { value: 'hourly', label: 'Taux horaire' },
          { value: 'daily', label: 'Taux journalier' },
          { value: 'fixed', label: 'Montant fixe pour la mission' },
          { value: 'milestone', label: 'Paiement par étapes' },
        ],
      },
      {
        id: '1-7',
        type: 'text',
        label: 'Montant de la rémunération (en €)',
        required: true,
        placeholder: 'Ex: 500',
      },
      {
        id: '1-8',
        type: 'checkbox',
        label: 'Clauses spécifiques à inclure',
        required: false,
        options: [
          { value: 'intellectual-property', label: 'Propriété intellectuelle' },
          { value: 'confidentiality', label: 'Confidentialité renforcée' },
          { value: 'non-compete', label: 'Non-concurrence' },
          { value: 'exclusivity', label: 'Exclusivité' },
        ],
      },
      {
        id: '1-9',
        type: 'file',
        label: 'Documents complémentaires',
        required: false,
        helpText: 'Vous pouvez joindre tout document utile à la préparation du contrat (cahier des charges, etc.)',
      },
    ],
  },
  {
    id: '2',
    contractId: '3',
    title: 'Questionnaire Accord de Confidentialité (NDA)',
    description: 'Ce questionnaire nous permettra de personnaliser votre accord de confidentialité selon vos besoins spécifiques.',
    questions: [
      {
        id: '2-1',
        type: 'text',
        label: 'Nom de la partie divulgatrice',
        required: true,
        placeholder: 'Entité qui partage les informations confidentielles',
      },
      {
        id: '2-2',
        type: 'text',
        label: 'Nom de la partie réceptrice',
        required: true,
        placeholder: 'Entité qui reçoit les informations confidentielles',
      },
      {
        id: '2-3',
        type: 'textarea',
        label: 'Nature des informations confidentielles',
        required: true,
        placeholder: 'Décrivez précisément les types d\'informations qui seront partagées',
      },
      {
        id: '2-4',
        type: 'select',
        label: 'Durée de l\'obligation de confidentialité',
        required: true,
        options: [
          { value: '1-year', label: '1 an' },
          { value: '2-years', label: '2 ans' },
          { value: '3-years', label: '3 ans' },
          { value: '5-years', label: '5 ans' },
          { value: 'indefinite', label: 'Durée indéterminée' },
        ],
      },
    ],
  },
];

const QuestionnaireBuilder: React.FC = () => {
  const [questionnaires, setQuestionnaires] = useState<Questionnaire[]>(mockQuestionnaires);
  const [selectedQuestionnaireId, setSelectedQuestionnaireId] = useState<string | null>(null);
  const [isPreviewMode, setIsPreviewMode] = useState(false);
  
  const selectedQuestionnaire = selectedQuestionnaireId 
    ? questionnaires.find(q => q.id === selectedQuestionnaireId) 
    : null;
  
  const contractsWithoutQuestionnaire = contracts.filter(
    contract => !questionnaires.some(q => q.contractId === contract.id)
  );
  
  const handleCreateQuestionnaire = (contractId: string) => {
    const contract = contracts.find(c => c.id === contractId);
    if (!contract) return;
    
    const newQuestionnaire: Questionnaire = {
      id: `new-${Date.now()}`,
      contractId,
      title: `Questionnaire ${contract.name}`,
      description: `Ce questionnaire nous permettra de personnaliser votre ${contract.name} selon vos besoins spécifiques.`,
      questions: [],
    };
    
    setQuestionnaires([...questionnaires, newQuestionnaire]);
    setSelectedQuestionnaireId(newQuestionnaire.id);
    setIsPreviewMode(false);
  };
  
  const handleAddQuestion = () => {
    if (!selectedQuestionnaire) return;
    
    const newQuestion: Question = {
      id: `${selectedQuestionnaire.id}-${Date.now()}`,
      type: 'text',
      label: 'Nouvelle question',
      required: false,
    };
    
    const updatedQuestionnaire = {
      ...selectedQuestionnaire,
      questions: [...selectedQuestionnaire.questions, newQuestion],
    };
    
    setQuestionnaires(
      questionnaires.map(q => q.id === selectedQuestionnaire.id ? updatedQuestionnaire : q)
    );
  };
  
  const handleUpdateQuestion = (questionId: string, updates: Partial<Question>) => {
    if (!selectedQuestionnaire) return;
    
    const updatedQuestions = selectedQuestionnaire.questions.map(question => 
      question.id === questionId ? { ...question, ...updates } : question
    );
    
    const updatedQuestionnaire = {
      ...selectedQuestionnaire,
      questions: updatedQuestions,
    };
    
    setQuestionnaires(
      questionnaires.map(q => q.id === selectedQuestionnaire.id ? updatedQuestionnaire : q)
    );
  };
  
  const handleDeleteQuestion = (questionId: string) => {
    if (!selectedQuestionnaire) return;
    
    const updatedQuestions = selectedQuestionnaire.questions.filter(
      question => question.id !== questionId
    );
    
    const updatedQuestionnaire = {
      ...selectedQuestionnaire,
      questions: updatedQuestions,
    };
    
    setQuestionnaires(
      questionnaires.map(q => q.id === selectedQuestionnaire.id ? updatedQuestionnaire : q)
    );
  };
  
  const handleMoveQuestion = (questionId: string, direction: 'up' | 'down') => {
    if (!selectedQuestionnaire) return;
    
    const questionIndex = selectedQuestionnaire.questions.findIndex(q => q.id === questionId);
    if (questionIndex === -1) return;
    
    const newIndex = direction === 'up' ? questionIndex - 1 : questionIndex + 1;
    if (newIndex < 0 || newIndex >= selectedQuestionnaire.questions.length) return;
    
    const updatedQuestions = [...selectedQuestionnaire.questions];
    const temp = updatedQuestions[questionIndex];
    updatedQuestions[questionIndex] = updatedQuestions[newIndex];
    updatedQuestions[newIndex] = temp;
    
    const updatedQuestionnaire = {
      ...selectedQuestionnaire,
      questions: updatedQuestions,
    };
    
    setQuestionnaires(
      questionnaires.map(q => q.id === selectedQuestionnaire.id ? updatedQuestionnaire : q)
    );
  };
  
  const handleAddOption = (questionId: string) => {
    if (!selectedQuestionnaire) return;
    
    const question = selectedQuestionnaire.questions.find(q => q.id === questionId);
    if (!question) return;
    
    const options = question.options || [];
    const newOption: QuestionOption = {
      value: `option-${options.length + 1}`,
      label: `Option ${options.length + 1}`,
    };
    
    handleUpdateQuestion(questionId, {
      options: [...options, newOption],
    });
  };
  
  const handleUpdateOption = (questionId: string, optionIndex: number, updates: Partial<QuestionOption>) => {
    if (!selectedQuestionnaire) return;
    
    const question = selectedQuestionnaire.questions.find(q => q.id === questionId);
    if (!question || !question.options) return;
    
    const updatedOptions = question.options.map((option, index) => 
      index === optionIndex ? { ...option, ...updates } : option
    );
    
    handleUpdateQuestion(questionId, {
      options: updatedOptions,
    });
  };
  
  const handleDeleteOption = (questionId: string, optionIndex: number) => {
    if (!selectedQuestionnaire) return;
    
    const question = selectedQuestionnaire.questions.find(q => q.id === questionId);
    if (!question || !question.options) return;
    
    const updatedOptions = question.options.filter((_, index) => index !== optionIndex);
    
    handleUpdateQuestion(questionId, {
      options: updatedOptions,
    });
  };
  
  const handleUpdateQuestionnaire = (updates: Partial<Questionnaire>) => {
    if (!selectedQuestionnaire) return;
    
    const updatedQuestionnaire = {
      ...selectedQuestionnaire,
      ...updates,
    };
    
    setQuestionnaires(
      questionnaires.map(q => q.id === selectedQuestionnaire.id ? updatedQuestionnaire : q)
    );
  };
  
  const handleSaveQuestionnaire = () => {
    // In a real app, this would make an API call
    toast.success('Questionnaire enregistré avec succès');
  };
  
  const getContractName = (contractId: string) => {
    const contract = contracts.find(c => c.id === contractId);
    return contract ? contract.name : 'Contrat inconnu';
  };
  
  const renderQuestionEditor = (question: Question) => {
    return (
      <div key={question.id} className="bg-white rounded-lg shadow-md p-4 mb-4">
        <div className="flex justify-between items-start mb-4">
          <h3 className="text-lg font-medium">{question.label}</h3>
          <div className="flex space-x-2">
            <button
              type="button"
              onClick={() => handleMoveQuestion(question.id, 'up')}
              className="text-gray-500 hover:text-gray-700"
              disabled={selectedQuestionnaire?.questions[0]?.id === question.id}
            >
              <ArrowUp className="h-5 w-5" />
            </button>
            <button
              type="button"
              onClick={() => handleMoveQuestion(question.id, 'down')}
              className="text-gray-500 hover:text-gray-700"
              disabled={selectedQuestionnaire?.questions[selectedQuestionnaire.questions.length - 1]?.id === question.id}
            >
              <ArrowDown className="h-5 w-5" />
            </button>
            <button
              type="button"
              onClick={() => handleDeleteQuestion(question.id)}
              className="text-red-500 hover:text-red-700"
            >
              <Trash2 className="h-5 w-5" />
            </button>
          </div>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Libellé de la question
            </label>
            <input
              type="text"
              className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              value={question.label}
              onChange={(e) => handleUpdateQuestion(question.id, { label: e.target.value })}
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Type de question
            </label>
            <select
              className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              value={question.type}
              onChange={(e) => handleUpdateQuestion(question.id, { type: e.target.value as QuestionType })}
            >
              <option value="text">Texte court</option>
              <option value="textarea">Texte long</option>
              <option value="select">Liste déroulante</option>
              <option value="radio">Boutons radio</option>
              <option value="checkbox">Cases à cocher</option>
              <option value="date">Date</option>
              <option value="file">Fichier</option>
            </select>
          </div>
        </div>
        
        <div className="flex items-center mb-4">
          <input
            type="checkbox"
            id={`required-${question.id}`}
            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
            checked={question.required}
            onChange={(e) => handleUpdateQuestion(question.id, { required: e.target.checked })}
          />
          <label htmlFor={`required-${question.id}`} className="ml-2 block text-sm text-gray-700">
            Question obligatoire
          </label>
        </div>
        
        {(question.type === 'text' || question.type === 'textarea') && (
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Texte d'exemple (placeholder)
            </label>
            <input
              type="text"
              className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              value={question.placeholder || ''}
              onChange={(e) => handleUpdateQuestion(question.id, { placeholder: e.target.value })}
            />
          </div>
        )}
        
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Texte d'aide
          </label>
          <input
            type="text"
            className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
            value={question.helpText || ''}
            onChange={(e) => handleUpdateQuestion(question.id, { helpText: e.target.value })}
          />
        </div>
        
        {(question.type === 'select' || question.type === 'radio' || question.type === 'checkbox') && (
          <div>
            <div className="flex justify-between items-center mb-2">
              <label className="block text-sm font-medium text-gray-700">
                Options
              </label>
              <button
                type="button"
                onClick={() => handleAddOption(question.id)}
                className="text-sm text-blue-600 hover:text-blue-500"
              >
                + Ajouter une option
              </button>
            </div>
            
            {(question.options || []).map((option, index) => (
              <div key={index} className="flex items-center mb-2">
                <input
                  type="text"
                  className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  value={option.label}
                  onChange={(e) => handleUpdateOption(question.id, index, { label: e.target.value })}
                  placeholder="Libellé de l'option"
                />
                <input
                  type="text"
                  className="block w-full ml-2 border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  value={option.value}
                  onChange={(e) => handleUpdateOption(question.id, index, { value: e.target.value })}
                  placeholder="Valeur de l'option"
                />
                <button
                  type="button"
                  onClick={() => handleDeleteOption(question.id, index)}
                  className="ml-2 text-red-500 hover:text-red-700"
                >
                  <Trash2 className="h-5 w-5" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };
  
  const renderQuestionPreview = (question: Question) => {
    return (
      <div key={question.id} className="mb-6">
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {question.label}
          {question.required && <span className="text-red-500 ml-1">*</span>}
        </label>
        
        {question.helpText && (
          <p className="text-sm text-gray-500 mb-2">{question.helpText}</p>
        )}
        
        {question.type === 'text' && (
          <input
            type="text"
            className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
            placeholder={question.placeholder}
            disabled
          />
        )}
        
        {question.type === 'textarea' && (
          <textarea
            rows={3}
            className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
            placeholder={question.placeholder}
            disabled
          />
        )}
        
        {question.type === 'select' && (
          <select
            className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
            disabled
          >
            <option value="">Sélectionner une option</option>
            {(question.options || []).map((option, index) => (
              <option key={index} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        )}
        
        {question.type === 'radio' && (
          <div className="space-y-2">
            {(question.options || []).map((option, index) => (
              <div key={index} className="flex items-center">
                <input
                  type="radio"
                  id={`preview-${question.id}-${index}`}
                  name={`preview-${question.id}`}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                  disabled
                />
                <label htmlFor={`preview-${question.id}-${index}`} className="ml-2 block text-sm text-gray-700">
                  {option.label}
                </label>
              </div>
            ))}
          </div>
        )}
        
        {question.type === 'checkbox' && (
          <div className="space-y-2">
            {(question.options || []).map((option, index) => (
              <div key={index} className="flex items-center">
                <input
                  type="checkbox"
                  id={`preview-${question.id}-${index}`}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  disabled
                />
                <label htmlFor={`preview-${question.id}-${index}`} className="ml-2 block text-sm text-gray-700">
                  {option.label}
                </label>
              </div>
            ))}
          </div>
        )}
        
        {question.type === 'date' && (
          <input
            type="date"
            className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
            disabled
          />
        )}
        
        {question.type === 'file' && (
          <input
            type="file"
            className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
            disabled
          />
        )}
      </div>
    );
  };
  
  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">Générateur de questionnaires</h1>
        
        {selectedQuestionnaire && (
          <div className="flex space-x-4">
            <button
              onClick={() => setIsPreviewMode(!isPreviewMode)}
              className="flex items-center bg-gray-100 hover:bg-gray-200 text-gray-800 font-medium py-2 px-4 rounded-md"
            >
              {isPreviewMode ? (
                <>
                  <Edit className="h-5 w-5 mr-2" />
                  Mode édition
                </>
              ) : (
                <>
                  <Eye className="h-5 w-5 mr-2" />
                  Aperçu
                </>
              )}
            </button>
            
            <button
              onClick={handleSaveQuestionnaire}
              className="flex items-center bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-md"
            >
              <Save className="h-5 w-5 mr-2" />
              Enregistrer
            </button>
          </div>
        )}
      </div>
      
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        <div className="lg:col-span-1">
          <div className="bg-white rounded-lg shadow-md p-4">
            <h2 className="text-lg font-semibold mb-4">Questionnaires</h2>
            
            <div className="space-y-2 mb-4">
              {questionnaires.map((questionnaire) => (
                <button
                  key={questionnaire.id}
                  onClick={() => {
                    setSelectedQuestionnaireId(questionnaire.id);
                    setIsPreviewMode(false);
                  }}
                  className={`block w-full text-left px-3 py-2 rounded-md ${
                    selectedQuestionnaireId === questionnaire.id
                      ? 'bg-blue-100 text-blue-700'
                      : 'hover:bg-gray-100'
                  }`}
                >
                  {questionnaire.title}
                </button>
              ))}
            </div>
            
            {contractsWithoutQuestionnaire.length > 0 && (
              <div>
                <h3 className="text-sm font-medium text-gray-700 mb-2">
                  Créer un nouveau questionnaire
                </h3>
                <select
                  className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  onChange={(e) => handleCreateQuestionnaire(e.target.value)}
                  value=""
                >
                  <option value="" disabled>
                    Sélectionner un contrat
                  </option>
                  {contractsWithoutQuestionnaire.map((contract) => (
                    <option key={contract.id} value={contract.id}>
                      {contract.name}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>
        </div>
        
        <div className="lg:col-span-3">
          {selectedQuestionnaire ? (
            isPreviewMode ? (
              <div className="bg-white rounded-lg shadow-md p-6">
                <h2 className="text-xl font-bold mb-2">{selectedQuestionnaire.title}</h2>
                <p className="text-gray-600 mb-6">{selectedQuestionnaire.description}</p>
                
                <form>
                  {selectedQuestionnaire.questions.map(renderQuestionPreview)}
                  
                  <div className="mt-8 flex justify-end">
                    <button
                      type="button"
                      className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-6 rounded-md"
                      disabled
                    >
                      Soumettre
                    </button>
                  </div>
                </form>
              </div>
            ) : (
              <div>
                <div className="bg-white rounded-lg shadow-md p-6 mb-6">
                  <h2 className="text-lg font-semibold mb-4">Informations générales</h2>
                  
                  <div className="grid grid-cols-1 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Contrat associé
                      </label>
                      <input
                        type="text"
                        className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm bg-gray-50"
                        value={getContractName(selectedQuestionnaire.contractId)}
                        disabled
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Titre du questionnaire
                      </label>
                      <input
                        type="text"
                        className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                        value={selectedQuestionnaire.title}
                        onChange={(e) => handleUpdateQuestionnaire({ title: e.target.value })}
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Description
                      </label>
                      <textarea
                        rows={3}
                        className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                        value={selectedQuestionnaire.description}
                        onChange={(e) => handleUpdateQuestionnaire({ description: e.target.value })}
                      />
                    </div>
                  </div>
                </div>
                
                <div className="mb-6">
                  <div className="flex justify-between items-center mb-4">
                    <h2 className="text-lg font-semibold">Questions</h2>
                    <button
                      onClick={handleAddQuestion}
                      className="flex items-center bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-md"
                    >
                      <Plus className="h-5 w-5 mr-2" />
                      Ajouter une question
                    </button>
                  </div>
                  
                  {selectedQuestionnaire.questions.length === 0 ? (
                    <div className="bg-white rounded-lg shadow-md p-6 text-center">
                      <p className="text-gray-500 mb-4">Aucune question dans ce questionnaire</p>
                      <button
                        onClick={handleAddQuestion}
                        className="text-blue-600 hover:text-blue-500 font-medium"
                      >
                        Ajouter votre première question
                      </button>
                    </div>
                  ) : (
                    selectedQuestionnaire.questions.map(renderQuestionEditor)
                  )}
                </div>
              </div>
            )
          ) : (
            <div className="bg-white rounded-lg shadow-md p-6 text-center">
              <p className="text-gray-500 mb-4">
                Sélectionnez un questionnaire existant ou créez-en un nouveau
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default QuestionnaireBuilder;